import {Component, forwardRef, Input, OnInit} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR, ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {environment} from '../../../../environments/environment';
import {
  densities, sd_bit_profiles, sd_pm_modes,
  protection_systems, default_protection_systems, STARDUSTMARK,
} from '../constants';
import {DlCheckboxComponent} from '../dl-checkbox/dl-checkbox.component';


/**
 * At least one system, and only ones the template knows.
 *
 * An empty list is not "no DRM" -- the template rejects it outright, because an encrypted output
 * advertising nothing is unplayable rather than unprotected. Turning encryption off is how you get
 * no DRM.
 */
const protectionSystemsValidator = (control: AbstractControl): ValidationErrors | null => {
  const chosen: string[] = control.value ?? [];
  if (!chosen.length) {
    return {noProtectionSystem: true};
  }
  const known = protection_systems.map(s => s.id);
  const unknown = chosen.filter(s => !known.includes(s));
  return unknown.length ? {unknownProtectionSystem: unknown.join(', ')} : null;
};

@Component({
    selector: 'app-vod-default-format-specific',
    templateUrl: './vod-default-format-specific.component.html',
    styleUrls: ['./vod-default-format-specific.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => VodDefaultFormatSpecificComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: VodDefaultFormatSpecificComponent,
            multi: true
        }
    ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DlCheckboxComponent,
  ]
})
export class VodDefaultFormatSpecificComponent implements OnInit, ControlValueAccessor {
  static DEFAULTS = {
    watermark: null,
    encrypt: true,
    protection_systems: default_protection_systems,
    // Every STARDUSTmark setting in one object, named as the template reads it. The `sd_` prefix
    // is gone with the flat keys it existed to keep apart, and `seed_auto` is the one member that
    // never leaves the UI -- see _applyTransforms.
    stardustmark: {
      bit_profile: sd_bit_profiles[1].id,
      wm_id: Math.floor(Math.random() * (sd_bit_profiles[1].max + 1)),
      strength: 4,
      fec: 2,
      pm_mode: sd_pm_modes[2].id,
      sp_width: 16,
      sp_height: 16,
      superpixel_density: 60,
      pixel_density: 60,
      seed: 0,
      seed_auto: true,
    },
    onboard: false,
    // PO-wide, like everything else in this card: thumbnails are a property of the delivery, and
    // every item of a PO wants the same answer. It used to be per item, which meant an operator
    // had to tick it once per title and could silently end up with a PO where some items had
    // thumbnails and some did not.
    thumbs: false,
    onboard_tags: [],
    onboard_window_start: null,
    onboard_window_end: null,
  };

  @Input()
  parentForm: FormGroup<any>;

  form: FormGroup;
  densities = densities;
  sd_bit_profiles = sd_bit_profiles;
  sd_pm_modes = sd_pm_modes;
  protection_systems = protection_systems;
  protected readonly STARDUSTMARK = STARDUSTMARK;

  /** The STARDUSTmark settings group, which the template reads as one `stardustmark` object. */
  get sm(): FormGroup { return this.form.get('stardustmark') as FormGroup; }

  protected readonly environment = environment;

  changed: any = () => {};
  touched: any = () => {};

  constructor(private formBuilder: FormBuilder) {
    this.form = this.formBuilder.group({
      // The picker's own value, and the only thing that decides whether a watermark is embedded:
      // null (No Watermark) or STARDUSTMARK. There used to be a separate watermark_enabled
      // checkbox with the algorithm derived from it; a value derived from two properties is
      // exactly what the one control removes.
      watermark: null,
      encrypt: true,
      // The set the packager advertises. Held as the full array rather than one checkbox per
      // system so it round-trips as the key the template reads.
      protection_systems: [[...default_protection_systems], protectionSystemsValidator],
      // One group, mirroring the `stardustmark` object the template reads, so the form shape and
      // the payload shape are the same thing. The group is disabled whenever the picker says No
      // Watermark, which is what keeps its settings out of the emitted value: a disabled control
      // contributes nothing to `form.value` and nothing to `form.valid` either, so a half-filled
      // seed cannot block a PO that embeds nothing.
      stardustmark: this.formBuilder.group({
        bit_profile: sd_bit_profiles[1].id,
        wm_id: [
          Math.floor(Math.random() * (sd_bit_profiles[1].max + 1)),
          Validators.compose([Validators.min(0), Validators.max(sd_bit_profiles[1].max), Validators.required])],
        strength: [4, [Validators.required, Validators.min(1), Validators.max(20)]],
        fec: [2, [Validators.required, Validators.min(1)]],
        pm_mode: [sd_pm_modes[0].id, [Validators.required]],
        sp_width: [16, [Validators.required, Validators.min(1)]],
        sp_height: [16, [Validators.required, Validators.min(1)]],
        superpixel_density: densities[5].id,
        pixel_density: densities[5].id,
        seed: [0, [Validators.required, Validators.min(0), Validators.max(31)]],
        // UI-only: an auto seed is `seed: -1`, which tells the embedder to derive it from the
        // image content. Never emitted -- see _applyTransforms.
        seed_auto: [true],
      }),
      onboard: false,
      thumbs: false,
      onboard_tags: [[]],
      onboard_window_start: [null],
      onboard_window_end: [null],
    });

    this.form.valueChanges.subscribe(() => {
      this.touched();

      // The ID has to fit the payload size, and the size is a field of its own.
      if (this.form.get('watermark').value === STARDUSTMARK) {
        const nuMax = sd_bit_profiles.find(pls => pls.id === this.sm.get('bit_profile').value).max;
        this.sm.get('wm_id').setValidators([Validators.min(0), Validators.max(nuMax), Validators.required]);
      }
      this.sm.get('wm_id').updateValueAndValidity({emitEvent: false});

      this._emit();
    });

    // The picker decides whether the STARDUSTmark group takes part at all, and the auto-seed
    // checkbox whether the seed field within it does. Both are pure enable/disable, so they are
    // one method called from the two places that can change them -- and from writeValue, which
    // patches with emitEvent: false and would otherwise leave a restored value in the wrong state.
    this.form.get('watermark').valueChanges.subscribe(() => this._syncStardustEnabled());
    this.sm.get('seed_auto').valueChanges.subscribe(() => this._syncStardustEnabled());
    this._syncStardustEnabled();

    this.form.get('onboard').valueChanges.subscribe((val) => {
      if (val) {
        this.form.controls['onboard_tags'].enable();
        this.form.controls['onboard_window_start'].enable();
        this.form.controls['onboard_window_end'].enable();
      } else {
        this.form.controls['onboard_tags'].disable();
        this.form.controls['onboard_window_start'].disable();
        this.form.controls['onboard_window_end'].disable();
      }
    });

    this.form.get('encrypt').valueChanges.subscribe((val) => {
      if (!val) {
        this.form.controls['protection_systems'].disable();
        this.form.controls['onboard'].disable();
        this.form.controls['onboard_tags'].disable();
        this.form.controls['onboard_window_start'].disable();
        this.form.controls['onboard_window_end'].disable();
      } else {
        this.form.controls['protection_systems'].enable();
        this.form.controls['onboard'].enable();
      }
    });
  }

  ngOnInit(): void {
    this._emit();
  }

  writeValue(value: any): void {
    if (value) {
      const patchVal = {...value};
      // Transform onboard_window object to separate fields for the form
      if (patchVal.onboard_window) {
        patchVal.onboard_window_start = patchVal.onboard_window.start_time;
        patchVal.onboard_window_end = patchVal.onboard_window.end_time;
        delete patchVal.onboard_window;
      }
      // Transform onboard_tags array to comma-separated string for the form
      if (patchVal.onboard_tags && Array.isArray(patchVal.onboard_tags)) {
        patchVal.onboard_tags = patchVal.onboard_tags.join(', ');
      }

      // 'conf' was the legacy spelling of the ladder. The template now refuses it outright rather
      // than ignoring it, so a restored form state carrying one is dropped here instead of being
      // migrated: its keys ('avc-profile', a flat rendition list) do not describe a group, and
      // guessing at one would silently encode something the operator never asked for.
      delete patchVal.conf;

      // Any watermark at all is STARDUSTmark -- this form offers nothing else, and the picker has
      // to have a value it can render or it would show blank for a value it cannot represent.
      patchVal.watermark = patchVal.watermark ? STARDUSTMARK : null;

      // seed: -1 is the auto seed, and the checkbox is how the form says so.
      if (patchVal.stardustmark) {
        const sm = {...patchVal.stardustmark};
        sm.seed_auto = sm.seed === undefined ? true : sm.seed === -1;
        sm.seed = sm.seed === -1 || sm.seed === undefined ? 0 : sm.seed;
        patchVal.stardustmark = sm;
      }

      this.form.patchValue(patchVal, {emitEvent: false});
      // patchValue does not emit, so nothing above ran the enable/disable rules for the value
      // just written.
      this._syncStardustEnabled();
    }
  }

  /** Is this system in the advertised set? */
  hasProtectionSystem(id: string): boolean {
    return (this.form.get('protection_systems').value ?? []).includes(id);
  }

  /** Toggle one system. The canonical order is settled on emission, for every value shape. */
  toggleProtectionSystem(id: string, on: boolean): void {
    const chosen = new Set<string>(this.form.get('protection_systems').value ?? []);
    if (on) { chosen.add(id); } else { chosen.delete(id); }
    this.form.get('protection_systems').setValue(
      protection_systems.map(s => s.id).filter(s => chosen.has(s)));
  }

  registerOnChange(fn: any): void {
    this.changed = fn;
  }

  registerOnTouched(fn: any): void {
    this.touched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  validate({value}: FormControl) {
    return this.form.valid ? null : {invalid: true};
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  /**
   * The STARDUSTmark group takes part only while the picker asks for a watermark, and the seed
   * field only while the operator states one by hand.
   *
   * Disabling is what keeps the settings out of the emitted value and out of validation, rather
   * than a delete in the transform: a disabled control contributes to neither.
   */
  private _syncStardustEnabled(): void {
    const marked = this.form.get('watermark').value === STARDUSTMARK;
    if (marked) {
      this.sm.enable({emitEvent: false});
    } else {
      this.sm.disable({emitEvent: false});
    }
    if (marked && !this.sm.get('seed_auto').value) {
      this.sm.get('seed').enable({emitEvent: false});
    } else {
      this.sm.get('seed').disable({emitEvent: false});
    }
  }

  private _emit(): void {
    const transformedVal = VodDefaultFormatSpecificComponent._applyTransforms({...this.form.value});
    VodDefaultFormatSpecificComponent._pruneProtectionSystems(transformedVal);
    VodDefaultFormatSpecificComponent._pruneOnboardKeys(transformedVal);
    this.changed(transformedVal);
  }

  /**
   * Normalizes one format_specific_data object into what the API should actually receive.
   *
   * Static and pure so the submit path can run it over whatever value currently sits in the outer
   * form control. That matters because the control is not always something this component emitted:
   * a restored form state (see `Po2024Service.getFormState`) is written straight into the FormArray,
   * and `writeValue` patches with `emitEvent: false`, so without a user touching the card no emission
   * ever happens and the stored object -- possibly in an older shape -- would be submitted verbatim.
   *
   * Every key it returns is PO-wide. Nothing is per item any more: `thumbs` was the last one, and
   * it moved here because a PO whose items disagree about thumbnails is not something anyone asked
   * for.
   */
  static forSubmit(value: any, encodingLadder: any = null): any {
    const val = VodDefaultFormatSpecificComponent._applyTransforms({...(value ?? {})});
    VodDefaultFormatSpecificComponent._pruneProtectionSystems(val);
    VodDefaultFormatSpecificComponent._pruneOnboardKeys(val);
    // A null ladder is not an empty ladder: leaving the key off is what makes the template render
    // its own default, and sending null would fail its validation ("must be an object").
    if (encodingLadder) {
      val.encoding_ladder = VodDefaultFormatSpecificComponent._ladderForEncryption(encodingLadder, !!val.encrypt);
    }
    return val;
  }

  /**
   * The ladder's DRM key tiers describe how an encrypted output is split across keys, by width.
   * With encryption off nothing is keyed at all, so the split describes nothing -- and the stored
   * PO is the only record of what ran, so it must not claim a tier boundary that never applied.
   *
   * The ladder handed in is the live PO form value, so this returns a copy rather than deleting
   * the key from it: the card would otherwise lose its tiers the moment someone submits, and
   * unticking encryption would silently reset them.
   */
  private static _ladderForEncryption(ladder: any, encrypt: boolean): any {
    if (encrypt || !ladder || !('drm_streamtypes' in ladder)) {
      return ladder;
    }
    const {drm_streamtypes, ...rest} = ladder;
    return rest;
  }

  /**
   * The DRM set only means anything for an encrypted item, and is only sent when it narrows the
   * template's own.
   *
   * Leaving the key off while it still matches keeps existing jobs on whatever the template
   * advertises, which is the same reasoning the encoding ladder applies to `audio_layouts`: a
   * future change there then reaches jobs without a UI release.
   */
  private static _pruneProtectionSystems(val: any): void {
    const chosen: string[] = Array.isArray(val.protection_systems) ? val.protection_systems : [];
    // Narrow to the systems this form knows, in the template's canonical order. Done here rather
    // than only where the checkboxes are toggled: forSubmit runs over whatever sits in the outer
    // control, which may be a restored form state this component never emitted -- and a value that
    // never passed through the form never met the validator either.
    const known = protection_systems.map(s => s.id).filter(s => chosen.includes(s));
    const isDefault = known.length === default_protection_systems.length
      && default_protection_systems.every(s => known.includes(s));
    // An empty set is not a request for "no DRM" -- the template rejects an empty list outright,
    // and turning encryption off is how you get no DRM. It means nobody has stated an opinion:
    // either a form state saved before this field existed, or a value carrying only systems this
    // form no longer offers. Both have to fall back to the template's own default, because the one
    // thing an empty list can never do is produce a working job.
    //
    // This is deliberately more forgiving than the form, which flags an empty selection as an
    // error. Someone unticking every box gets told; a stored object with nothing to say does not
    // get a broken PO.
    if (!val.encrypt || !known.length || isDefault) {
      delete val.protection_systems;
      return;
    }
    val.protection_systems = known;
  }

  /** Onboarding only means anything for an encrypted, onboarding-enabled item. */
  private static _pruneOnboardKeys(val: any): void {
    if (!val.encrypt || !val.onboard) {
      delete val.onboard_tags;
      delete val.onboard_window;
    }
    if (!val.encrypt) {
      delete val.onboard;
    }
  }

  private static _applyTransforms(val: any): any {
    // The ladder is owned by the PO, not the item -- forSubmit grafts it back on. Anything left
    // here comes from a form state saved while it was still per item.
    delete val.encoding_ladder;
    // Rejected outright by the template — see writeValue.
    delete val.conf;

    // Transform onboard fields if encryption is enabled AND onboard is enabled
    if (val.encrypt && val.onboard) {
      if (val.onboard_window_start && val.onboard_window_end) {
        val.onboard_window = {
          start_time: VodDefaultFormatSpecificComponent._formatDateTimeForBackend(val.onboard_window_start),
          end_time: VodDefaultFormatSpecificComponent._formatDateTimeForBackend(val.onboard_window_end)
        };
      }
      if (typeof val.onboard_tags === 'string') {
        val.onboard_tags = val.onboard_tags.split(',').map(t => t.trim()).filter(t => t !== '');
      } else if (!Array.isArray(val.onboard_tags)) {
        val.onboard_tags = [];
      }
    }
    delete val.onboard_window_start;
    delete val.onboard_window_end;

    // Any watermark is STARDUSTmark, or there is none: this form implements one algorithm and the
    // template accepts exactly the two values. Normalised rather than passed through, because an
    // unknown value fails the render -- and every value that ever meant "a Castlabs single-frame
    // watermark" means this one.
    const marked = !!val.watermark;
    val.watermark = marked ? STARDUSTMARK : null;

    // The settings belong to the watermark, so they travel with it and nowhere else. Usually there
    // is nothing to drop -- the form disables the whole group, so an unmarked value never carries
    // one -- but this also runs over values that never passed through the form.
    if (!marked) {
      delete val.stardustmark;
    } else {
      // seed_auto is UI-only: -1 tells the embedder to derive the seed from the image content.
      const {seed_auto, ...sm} = val.stardustmark ?? {};
      val.stardustmark = {...sm, seed: seed_auto ? -1 : Number(sm.seed)};
    }

    return val;
  }


  private static _formatDateTimeForBackend(dateTimeLocalValue: string): string {
    if (!dateTimeLocalValue) return null;
    // Add seconds if not present and append Z for UTC
    return dateTimeLocalValue.includes(':') && dateTimeLocalValue.split(':').length === 2
      ? `${dateTimeLocalValue}:00Z`
      : `${dateTimeLocalValue}Z`;
  }


}
